/**
 * Per-route SEO copy, lifted verbatim from the source marketing pages in
 * ~/Desktop/LightShipSmart (their <title>, meta description and Service schema). Keep it
 * here rather than in the page components so titles and descriptions can be reviewed as a
 * set, that is how duplicates and missing entries get spotted.
 */
export type LandingSeoEntry = {
  title: string;
  description: string;
  /**
   * Set to `false` while a route is unfinished or has no useful public content. The
   * metadata, sitemap, and page-level structured data all honor this single flag.
   */
  indexable?: boolean;
  /** Optional social copy when the share card should differ from search metadata. */
  socialTitle?: string;
  socialDescription?: string;
  /**
   * Set when this route serves the same page as another one. The canonical then points at
   * the primary route so the two URLs are not indexed as duplicate content.
   */
  canonicalOf?: string;
  /** schema.org Service fields; absent for pages that are not a service offering. */
  serviceType?: string;
  serviceName?: string;
  serviceDescription?: string;
  /** `false` omits areaServed when the service area must be qualified per request. */
  areaServed?: string | false;
};

/** Keyed by `section/slug`, plus `home` for the landing root. */
export const landingSeo: Record<string, LandingSeoEntry> = {
  'industries/manufacturing': {
    title: 'Manufacturing Logistics Services | Freight & Supply',
    description:
      'Manufacturing logistics services with scheduled owned-fleet lanes for inbound materials and outbound finished goods — dry van, flatbed, and reefer across 48 states.',
    serviceType: 'Manufacturing Logistics',
    serviceName: 'Manufacturing Logistics Services',
    serviceDescription:
      'Asset-based manufacturing logistics services with scheduled dedicated lanes for inbound materials and outbound finished goods across dry van, flatbed, and reefer, nationwide.',
  },
  'industries/food-beverage': {
    title: 'Food & Beverage Logistics | Cold-Chain Trucking',
    description:
      'Food and beverage logistics on our own food-grade reefer fleet — cold-chain transportation and warehousing for grocery, produce, and beverage across 48 states.',
    serviceType: 'Food and Beverage Logistics',
    serviceName: 'Food and Beverage Logistics',
    serviceDescription:
      'Asset-based food and beverage logistics on an owned food-grade reefer fleet, with cold-chain transportation and refrigerated warehousing for grocery, produce, dairy, and beverage across all 48 states.',
  },
  'industries/retail-ecommerce': {
    title: 'Retail Logistics Services | Freight & Distribution',
    description:
      'Retail logistics services with committed capacity that protects on-time delivery through demand peaks — freight, fulfillment, and distribution across 48 states.',
    serviceType: 'Retail Logistics',
    serviceName: 'Retail Logistics Services',
    serviceDescription:
      'Asset-based retail logistics services with dedicated capacity, e-commerce fulfillment, and distribution to protect on-time delivery through demand peaks across all 48 states.',
  },
  'industries/construction': {
    title: 'Construction Logistics Services | Materials & Equipment',
    description:
      'Construction logistics services for building materials and heavy equipment — flatbed, step deck, and heavy-haul transport with jobsite delivery across 48 states.',
    serviceType: 'Construction Logistics',
    serviceName: 'Construction Logistics Services',
    serviceDescription:
      'Asset-based construction logistics services for building materials and heavy equipment via flatbed, step deck, and heavy haul, with permitting and scheduled jobsite delivery across all 48 states.',
  },
  'industries/automotive': {
    title: 'Automotive Logistics | JIT Parts & Freight Shipping',
    description:
      'Automotive logistics with the timing precision assembly lines require — just-in-time parts, tire distribution, and dry van freight across all 48 states. Get a quote.',
    serviceType: 'Automotive Logistics',
    serviceName: 'Automotive Logistics',
    serviceDescription:
      'Asset-based automotive logistics with dedicated just-in-time lanes, tire distribution, and expedited backup for time-critical parts across all 48 states.',
  },
  'industries/government': {
    title: 'Government Logistics Services | Public Sector Freight',
    description:
      'Government logistics services with the compliance, reliability, and accountability public-sector freight requires — asset-based capacity across all 48 states.',
    serviceType: 'Government Logistics',
    serviceName: 'Government Logistics Services',
    serviceDescription:
      'Government logistics services for public-sector agencies and contractors, with compliance, documentation, and asset-based reliability across all 48 states.',
  },
  'industries/pharmaceutical': {
    title: 'Pharmaceutical Logistics Services | Cold-Chain Pharma',
    description:
      'Pharmaceutical logistics services with validated cold-chain transport on our own reefer fleet — GDP-aligned handling and temperature data across all 48 states.',
    serviceType: 'Pharmaceutical Logistics',
    serviceName: 'Pharmaceutical Logistics Services',
    serviceDescription:
      'Asset-based pharmaceutical logistics services with validated cold-chain transport on an owned reefer fleet, GDP-aligned handling, and retrievable temperature data across all 48 states.',
  },
  'industries/technology-electronics': {
    title: 'Electronics Logistics Services | High-Tech Freight',
    description:
      'Electronics logistics services for high-value, fragile, and time-sensitive tech freight — careful handling, JIT capacity, and full visibility across 48 states.',
    serviceType: 'Electronics Logistics',
    serviceName: 'Electronics and High-Tech Freight Logistics',
    serviceDescription:
      'Asset-based electronics logistics services for high-value, fragile, and time-sensitive technology freight, with careful handling, just-in-time capacity, and real-time visibility across all 48 states.',
  },
  'industries/packaging': {
    title: 'Packaging Logistics | Freight for Packaging Suppliers',
    description:
      'Packaging logistics for manufacturers and suppliers — dry van freight for cartons, containers, films, and materials, with dedicated capacity across all 48 states.',
    serviceType: 'Packaging Logistics',
    serviceName: 'Packaging Logistics',
    serviceDescription:
      'Packaging logistics for manufacturers and suppliers, moving cartons, containers, films, and raw materials on dedicated, scheduled dry van lanes across all 48 states.',
  },
  'shippers/managed-transportation': {
    title: 'Managed Transportation Services | Asset-Based 3PL',
    description:
      'Managed transportation services from an asset-based provider, one partner for planning, execution, and visibility, anchored by trucks we operate.',
  },
  'shippers/freight-brokerage': {
    title: 'Freight Brokerage | Vetted Overflow Capacity',
    description:
      'Freight brokerage that extends our own fleet, vetted carrier network, double-brokering protection, and one accountable team when demand exceeds our trucks.',
    serviceType: 'Freight Brokerage',
    serviceName: 'Freight Brokerage and Vetted Carrier Network',
    serviceDescription:
      'Asset-based freight brokerage that extends an owned fleet with a vetted carrier network for overflow, off-network lanes, and specialized equipment, with double-brokering protection and consistent visibility across all 48 states.',
  },
  'shippers/warehousing': {
    title: 'Warehousing Services | 3PL Distribution & Fulfillment',
    description:
      'Warehousing services connected to our own trucks, 3PL storage, cold storage, cross-docking, and fulfillment with transportation under one roof. Get a quote.',
    serviceType: 'Warehousing and Fulfillment',
    serviceName: 'Warehousing, Distribution, and Fulfillment Services',
    serviceDescription:
      '3PL warehousing, cold storage, cross-docking, pick-and-pack, order fulfillment, and inventory management connected directly to an asset-based transportation fleet, based in Naperville, Illinois.',
  },
  'shippers/dedicated-capacity': {
    title: 'Dedicated Capacity | Committed Trucks on Your Lanes',
    description:
      'Dedicated capacity from an asset-based carrier, committed trucks and drivers on your lanes, because we own them. Guaranteed coverage across 48 states. Learn more.',
    serviceType: 'Dedicated Capacity',
    serviceName: 'Dedicated Capacity and Contract Carriage',
    serviceDescription:
      "Asset-based dedicated capacity reserving committed trucks and drivers on a shipper's lanes under contract, with stable pricing and guaranteed coverage across all 48 states, supplemented by a vetted carrier network for overflow.",
  },
  'shippers/capacity-solutions': {
    title: 'Freight Capacity Solutions | Asset-Based & Flexible',
    description:
      'Freight capacity solutions from an asset-based carrier, dedicated, managed, brokerage, and expedited options that start with trucks we own. Find the right fit.',
    serviceType: 'Freight Capacity Solutions',
    serviceName: 'Freight Capacity Solutions',
    serviceDescription:
      'Asset-based freight capacity solutions including dedicated capacity, managed transportation, brokerage overflow, and expedited service, all anchored by an owned fleet across all 48 states.',
  },
  'shippers/project-logistics': {
    title: 'Project Logistics Services | Complex Freight Moves',
    description:
      'Project logistics services for complex, multi-shipment, and oversized freight moves, planning, permitting, and coordinated delivery managed by one team.',
    serviceType: 'Project Logistics',
    serviceName: 'Project Logistics Services',
    serviceDescription:
      'Project logistics services for complex, multi-shipment, and oversized freight moves, including planning, permitting, and sequenced delivery coordinated by one team across all 48 states.',
  },
  'shippers/international-logistics': {
    title: 'International Logistics Services | Cross-Border Freight',
    description:
      'International logistics services with cross-border freight to Mexico and Canada, port drayage coordination, and a U.S. asset-based fleet for the domestic leg.',
    serviceType: 'International Logistics',
    serviceName: 'International and Cross-Border Logistics',
    serviceDescription:
      'Cross-border freight to Mexico and Canada, port and rail drayage coordination, and customs handoff through partners, with the U.S. domestic leg supported by an owned asset-based fleet.',
  },
  'shippers/expedited-freight': {
    // Same page as services/expedited; the source site canonicalises it under services.
    canonicalOf: 'services/expedited',
    title: 'Expedited Freight Services | Time-Critical Trucking',
    description:
      'Expedited freight services for time-critical shipments, team-driver capacity and direct routing to hit tight windows across all 48 states. Get a fast quote.',
    serviceType: 'Expedited Freight',
    serviceName: 'Expedited Freight Services',
    serviceDescription:
      'Expedited freight services for time-critical shipments using team drivers, direct routing, and priority dispatch to hit tight delivery windows across all 48 states.',
  },
  'carriers/become-a-carrier': {
    title: 'Become a Carrier | Haul for Ship Smart Solutions',
    description:
      'Become a carrier for Ship Smart Solutions. Join a vetted network, get consistent freight and available loads, quick setup, and reliable pay. Apply today.',
    serviceType: 'Carrier Recruitment',
    serviceName: 'Become a Carrier',
    serviceDescription:
      "Carrier onboarding to join Ship Smart Solutions' vetted carrier network, offering consistent freight and available loads across dry van, reefer, and open deck nationwide.",
  },
  'carriers/available-loads': {
    title: 'Available Freight Loads | Loads for Carriers',
    description:
      'Find available freight loads with Ship Smart Solutions, consistent dry van, reefer, and open-deck freight for vetted carriers across all 48 states. Get set up.',
    serviceType: 'Freight Loads for Carriers',
    serviceName: 'Available Freight Loads',
    serviceDescription:
      "Available freight loads for vetted carriers across dry van, reefer, and open-deck equipment nationwide, sourced from an asset-based fleet's overflow and specialized lanes.",
  },
  'carriers/carrier-setup-packet': {
    indexable: false,
    title: 'Carrier Setup Packet | Ship Smart Solutions',
    description:
      'Complete the Ship Smart Solutions carrier setup packet to begin onboarding. Submit your company, authority, insurance, equipment, contact, and payment information for review.',
    serviceType: 'Carrier Onboarding',
    serviceName: 'Carrier Setup Packet',
    serviceDescription:
      'Carrier onboarding and setup packet for motor carriers seeking to haul freight with Ship Smart Solutions.',
  },
  'carriers/insurance-requirements': {
    title: 'Carrier Insurance Requirements | Ship Smart Solutions',
    description:
      'Carrier insurance requirements for hauling with Ship Smart Solutions, auto liability, cargo, and coverage minimums you need before setup. See the details.',
    serviceType: 'Carrier Insurance Requirements',
    serviceName: 'Carrier Insurance Requirements',
    serviceDescription:
      'Insurance requirements for carriers hauling with Ship Smart Solutions, including auto liability, cargo, and reefer breakdown coverage minimums.',
  },
  'carriers/carrier-benefits': {
    title: 'Carrier Benefits | Why Haul for Ship Smart Solutions',
    description:
      'Carrier benefits with Ship Smart Solutions, consistent freight, reliable and quick pay, fair treatment, and nationwide loads from a carrier that runs its own trucks.',
    serviceType: 'Carrier Benefits',
    serviceName: 'Carrier Benefits',
    serviceDescription:
      'Carrier benefits with Ship Smart Solutions including consistent freight, reliable settlement, quick-pay options where available, fair treatment, and nationwide loads from an asset-based carrier.',
  },
  'services/dry-van': {
    title: 'Dry Van Trucking | Dedicated 53′ Full Truckload',
    description:
      'Dry van trucking on our own dedicated 53′ fleet. Reliable full-truckload freight for retail and manufacturing across all 48 states. Get a dry van quote.',
    serviceType: 'Dry Van Trucking',
    serviceName: 'Dry Van Trucking and Full-Truckload Freight',
    serviceDescription:
      "Asset-based dry van trucking on a dedicated 53' fleet for full-truckload retail, manufacturing, and packaged-goods freight across all 48 states, with scheduled appointment-based delivery.",
  },
  'services/flatbed': {
    title: 'Flatbed Trucking | Step Deck & Open Deck Freight',
    description:
      'Flatbed trucking and open-deck freight for steel, machinery, and building materials. Flatbed, step deck, and RGN capacity across all 48 states. Get a quote.',
    serviceType: 'Flatbed Trucking',
    serviceName: 'Flatbed Trucking and Open-Deck Freight',
    serviceDescription:
      'Flatbed, step deck, and open-deck freight transport for steel, machinery, and building materials across all 48 states, including securement, tarping, and permitting.',
  },
  'services/reefer': {
    title: 'Reefer Trucking | Dedicated 53′ Refrigerated Carrier',
    description:
      'Reefer trucking on our own dedicated 53′ refrigerated fleet. Temperature-controlled, food-grade freight across all 48 states. Get a reefer freight quote.',
    serviceType: 'Reefer Trucking',
    serviceName: 'Reefer Trucking and Refrigerated Freight',
    serviceDescription:
      "Asset-based reefer trucking on a dedicated 53' refrigerated fleet for frozen, fresh, and temperature-sensitive freight across all 48 states, with continuous temperature monitoring and food-grade handling.",
  },
  'services/heavy-haul': {
    title: 'Heavy Haul Trucking | Oversized & Over-Dimensional',
    description:
      'Heavy haul trucking for oversized and over-dimensional freight, RGN, lowboy, and multi-axle transport with permits and route planning across all 48 states.',
    serviceType: 'Heavy Haul Trucking',
    serviceName: 'Heavy Haul and Oversized Transport',
    serviceDescription:
      'Heavy haul trucking for oversized and over-dimensional freight using RGN, lowboy, and multi-axle equipment, with permitting, route planning, and escorts managed across all 48 states.',
  },
  'services/drayage': {
    title: 'Drayage Services | Port & Rail Container Transport',
    description:
      'Drayage services for port and rail container transport, moving containers to warehouses and inland destinations, connected to 3PL warehousing and 48-state freight.',
    serviceType: 'Drayage',
    serviceName: 'Drayage Services',
    serviceDescription:
      'Drayage services for port and rail container transport, transloading, and container-to-warehouse moves connected to 3PL warehousing and 48-state transportation.',
  },
  'services/expedited': {
    title: 'Expedited Freight Services | Time-Critical Trucking',
    description:
      'Expedited freight services for time-critical shipments, team-driver capacity and direct routing to hit tight windows across all 48 states. Get a fast quote.',
    serviceType: 'Expedited Freight',
    serviceName: 'Expedited Freight Services',
    serviceDescription:
      'Expedited freight services for time-critical shipments using team drivers, direct routing, and priority dispatch to hit tight delivery windows across all 48 states.',
  },
  'services/full-truckload': {
    title: 'Full Truckload (FTL) Shipping | Ship Smart Solutions',
    description:
      'Move dedicated truckload freight with coordinated capacity, equipment options, and shipment visibility. Request an FTL quote from Ship Smart Solutions.',
    socialTitle: 'Full Truckload Shipping for Dedicated Capacity',
    socialDescription:
      'A practical truckload solution for large, sensitive, or time-critical shipments.',
    serviceType: 'Full Truckload Shipping',
    serviceName: 'Full Truckload (FTL) Shipping',
    serviceDescription:
      'Shipment-specific full truckload planning with dedicated capacity and dry van, reefer, flatbed, or heavy-haul equipment options subject to confirmed availability.',
    areaServed: false,
  },
  'services/less-than-truckload': {
    title: 'Less Than Truckload (LTL) Shipping | Ship Smart',
    description:
      'Ship smaller freight with a coordinated LTL solution. Share capacity, manage pickup details, and request a tailored quote from Ship Smart Solutions.',
    socialTitle: 'LTL Freight Shipping for Smaller Loads',
    socialDescription:
      'A practical shared-capacity option for palletized freight that does not need a dedicated trailer.',
    serviceType: 'Less Than Truckload Shipping',
    serviceName: 'Less Than Truckload (LTL) Shipping',
    serviceDescription:
      'Coordinated shared-network transportation for eligible palletized and boxed freight, with shipment-specific classification, packaging, and accessorial review.',
    areaServed: false,
  },
  'services/partial-truckload': {
    title: 'Partial Truckload (PTL) Shipping | Ship Smart',
    description:
      'Explore a flexible middle ground between LTL and FTL. Ship Smart Solutions can review PTL options for larger or handling-sensitive freight.',
    socialTitle: 'Partial Truckload Shipping Between LTL and FTL',
    socialDescription:
      'A flexible freight option for shipments that need more space or different handling than standard LTL.',
    serviceType: 'Partial Truckload Shipping',
    serviceName: 'Partial Truckload (PTL) Shipping',
    serviceDescription:
      'Shipment-specific evaluation of partial truckload capacity for larger, bulky, or handling-sensitive freight that may not require a dedicated trailer.',
    areaServed: false,
  },
  'services/intermodal': {
    title: 'Intermodal Transportation Services | Ship Smart',
    description:
      'Coordinate truck and rail freight with an intermodal plan built around your origin, destination, terminals, equipment, and delivery requirements.',
    socialTitle: 'Coordinated Intermodal Freight Transportation',
    socialDescription:
      'Plan truck, rail, terminal, and delivery handoffs around the shipment.',
    serviceType: 'Intermodal Transportation',
    serviceName: 'Intermodal Transportation Services',
    serviceDescription:
      'Intermodal transportation planning that can coordinate approved truck, rail, terminal, drayage, documentation, and final-delivery handoffs.',
    areaServed: false,
  },
  'services/last-mile-delivery': {
    title: 'Last Mile Delivery Services | Ship Smart Solutions',
    description:
      'Coordinate final-leg deliveries from a warehouse, terminal, or local hub to the customer. Request a last-mile delivery review from Ship Smart Solutions.',
    socialTitle: 'Last Mile Delivery Coordination',
    socialDescription:
      'Connect inventory, delivery windows, accessorial requirements, and the final customer handoff.',
    serviceType: 'Last Mile Delivery',
    serviceName: 'Last Mile Delivery Services',
    serviceDescription:
      'Qualified final-leg freight coordination from an approved warehouse, terminal, distribution center, store, or local hub to the receiving destination.',
    areaServed: false,
  },
  'services/sustainable-green-logistics': {
    title: 'Sustainable Logistics Solutions | Ship Smart',
    description:
      'Build a more efficient freight plan with practical routing, capacity, and reporting considerations. Discuss sustainable logistics with Ship Smart Solutions.',
    socialTitle: 'Sustainable Logistics Built on Measurable Practices',
    socialDescription:
      'Evaluate freight efficiency, mode choices, routing, and reporting with a practical logistics partner.',
    serviceType: 'Sustainable Logistics Planning',
    serviceName: 'Sustainable and Green Logistics Review',
    serviceDescription:
      'A practical freight-efficiency review covering available routing, consolidation, mode, capacity, warehouse-handoff, and reporting considerations without unmeasured environmental promises.',
    areaServed: false,
  },
  'company/about': {
    title: 'About Ship Smart Solutions | Asset-Based 3PL',
    description:
      'About Ship Smart Solutions, an asset-based trucking company and 3PL in Naperville, IL, moving reefer, dry van, and open-deck freight across all 48 states.',
  },
  'company/carrier-network': {
    title: 'Verified Carrier Network | Vetted Freight Capacity',
    description:
      'Our verified carrier network extends our own fleet with vetted, compliance-monitored carriers, double-brokering protection and consistent visibility nationwide.',
    serviceType: 'Verified Carrier Network',
    serviceName: 'Verified Carrier Network',
    serviceDescription:
      'A verified carrier network of vetted, compliance-monitored carriers extending an asset-based fleet with double-brokering protection and consistent visibility nationwide.',
  },
  'company/case-studies': {
    title: 'Freight Case Studies | Ship Smart Solutions Results',
    description:
      'Freight case studies from Ship Smart Solutions, real results in cold-chain reliability, peak-season capacity, and oversized transport across the 48 states.',
  },
  'company/locations': {
    title: 'Trucking Company Chicago & Nationwide | Locations',
    description:
      'A Chicago-area trucking company serving all 48 states, reefer, dry van, and open-deck freight and 3PL warehousing from our Naperville, IL hub. See coverage.',
  },
  'company/real-time-tracking': {
    title: 'Real-Time Freight Tracking & Shipment Visibility',
    description:
      'Real-time freight tracking from trucks we operate directly. Track your shipment by reference number with live GPS location and delivery updates across 48 states.',
    serviceType: 'Freight Tracking and Shipment Visibility',
    serviceName: 'Real-Time Freight Tracking',
    serviceDescription:
      'Real-time freight tracking and shipment visibility through a client portal, with live GPS location, reference-number lookup, ETAs, and delivery documents, sourced largely from directly operated equipment.',
  },
  'company/shipment-visibility': {
    title: 'Supply Chain Visibility | Proactive Shipment Insight',
    description:
      'Supply chain visibility that goes beyond tracking, proactive exception alerts, ETAs, and reporting across all your freight, backed by an asset-based fleet.',
    serviceType: 'Supply Chain Visibility',
    serviceName: 'Supply Chain Visibility',
    serviceDescription:
      'Supply chain visibility with proactive exception alerts, predictive ETAs, and reporting across all freight, backed by data from an asset-based fleet.',
  },
  'company/digital-freight-management': {
    title: 'Freight Visibility Platform | Digital Freight Mgmt',
    description:
      'A freight visibility platform that connects tracking, TMS data, and reporting in one place, digital freight management backed by an asset-based fleet. See how.',
    serviceType: 'Digital Freight Management',
    serviceName: 'Freight Visibility Platform and Digital Freight Management',
    serviceDescription:
      'A freight visibility platform and digital freight management system consolidating tracking, TMS connectivity, and performance reporting, backed by an asset-based fleet and vetted carrier network.',
  },
  'company/news': {
    indexable: false,
    title: 'Freight News & Logistics Insights | Ship Smart Solutions',
    description:
      'Read freight news, shipping guides, market insights, and practical logistics strategies covering capacity, equipment, tracking, and transportation management.',
  },
  'company/guides': {
    title: 'Freight Shipping Guides | How to Ship Smarter',
    description:
      'Freight shipping guides from an asset-based carrier, how-to guides on reefer, flatbed, dry van, costs, and capacity to help you ship smarter. Start learning.',
  },
  'company/faq': {
    title: 'Freight & Logistics FAQ | Ship Smart Solutions',
    description:
      'Find answers about Ship Smart Solutions, service coverage, freight equipment, quotes, shipment tracking, carrier onboarding, insurance, and payment.',
  },
  home: {
    title: 'Asset-Based Logistics & 3PL | Ship Smart Solutions',
    description:
      'Asset-based trucking, managed transportation, warehousing, and real-time freight visibility across all 48 states. Get a quote from Ship Smart Solutions.',
  },
};
